// A chat message's attachment: images and video inline, everything else as a
// download chip.
//
// ── WHY THIS IS SHARED ──────────────────────────────────────────────────────
// It lived inside the Chat page, so the floating dock rendered an attachment
// message as an EMPTY bubble with only a timestamp — the screenshot somebody
// pasted was invisible to everyone reading in the dock. One renderer, both
// surfaces, and a message can never again look different depending on which
// window it is read in.

import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { FileText, Download } from "lucide-react";

/** The attachment columns on hq_chat_messages — all either surface needs. */
export interface AttachmentMsg {
  attachment_path?: string | null;
  attachment_name?: string | null;
  attachment_type?: string | null;
  attachment_size?: number | null;
}

/** One shared attachment-size rule for every composer. */
export const MAX_ATTACH_BYTES = 25 * 1024 * 1024; // 25 MB

// Cache signed URLs so we don't refetch on every render.
const attachUrlCache = new Map<string, { url: string; expires_at: number }>();

export function formatBytes(n: number | null | undefined): string {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Claim an image off a paste event, named for what it is.
 *
 * Returns null for a text paste, which the caller must leave to the browser.
 */
export function pastedImage(e: React.ClipboardEvent): File | null {
  const img = Array.from(e.clipboardData?.items ?? []).find(it => it.type.startsWith("image/"));
  const f = img?.getAsFile();
  if (!f) return null;
  const ext = (f.type.split("/")[1] || "png").replace("jpeg", "jpg");
  const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");
  return new File([f], `screenshot-${stamp}.${ext}`, { type: f.type });
}

export function ChatAttachment({ msg, isMe }: { msg: AttachmentMsg; isMe: boolean }) {
  const path = msg.attachment_path;
  const [url, setUrl] = useState<string | null>(() => {
    if (!path) return null;
    const c = attachUrlCache.get(path);
    return c && c.expires_at > Date.now() ? c.url : null;
  });
  useEffect(() => {
    if (!path) { setUrl(null); return; }
    const c = attachUrlCache.get(path);
    if (c && c.expires_at > Date.now()) { setUrl(c.url); return; }
    let cancelled = false;
    void supabase.storage.from("chat-attachments").createSignedUrl(path, 3600).then(({ data }) => {
      if (cancelled) return;
      if (data?.signedUrl) {
        attachUrlCache.set(path, { url: data.signedUrl, expires_at: Date.now() + 3500 * 1000 });
        setUrl(data.signedUrl);
      }
    });
    return () => { cancelled = true; };
  }, [path]);

  if (!path) return null;
  const isImage = (msg.attachment_type ?? "").startsWith("image/");
  const isVideo = (msg.attachment_type ?? "").startsWith("video/");

  if (isImage) {
    return url ? (
      <a href={url} target="_blank" rel="noreferrer" className="mt-1 block">
        <img src={url} alt={msg.attachment_name ?? "image"} loading="lazy" className="max-h-64 rounded-lg object-cover" />
      </a>
    ) : <div className="mt-1 h-32 w-48 animate-pulse rounded-lg bg-black/10" />;
  }
  if (isVideo) {
    return url ? (
      <video src={url} controls className="mt-1 max-h-64 rounded-lg" />
    ) : <div className="mt-1 h-32 w-48 animate-pulse rounded-lg bg-black/10" />;
  }
  return (
    <a
      href={url ?? undefined}
      target="_blank"
      rel="noreferrer"
      download={msg.attachment_name ?? undefined}
      className={`mt-1 flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${isMe ? "bg-white/15 text-white" : "bg-offwhite text-charcoal"}`}
    >
      <FileText size={18} className="shrink-0" />
      <span className="min-w-0 flex-1 truncate">{msg.attachment_name ?? "file"}</span>
      <span className={`shrink-0 text-xs ${isMe ? "text-white/70" : "text-gray2"}`}>{formatBytes(msg.attachment_size)}</span>
      <Download size={14} className="shrink-0" />
    </a>
  );
}
